import { useMemo, useState } from "react";
import toast from "react-hot-toast";
import axiosInstance from "../../../axiosInstance";
import { getJobPortalAuthConfig } from "../../../helpers/jobPortalAuth";
import Navbar from "../../components/Navbar";
import Sidebar from "../../components/Sidebar";

const audienceOptions = [
  {
    value: "all",
    label: "All Users",
    icon: "ri-group-line",
    note: "Everyone with an account",
  },
  {
    value: "recruiters",
    label: "Recruiters",
    icon: "ri-building-4-line",
    note: "Hiring teams and employers",
  },
  {
    value: "job_seekers",
    label: "Job Seekers",
    icon: "ri-briefcase-4-line",
    note: "Candidates looking for work",
  },
];

const initialForm = {
  audience: "all",
  title: "",
  body: "",
  actionUrl: "",
  imageUrl: "",
  dataKey: "",
  dataValue: "",
};

const NotificationCenter = () => {
  const [form, setForm] = useState(initialForm);
  const [sending, setSending] = useState(false);
  const [preview, setPreview] = useState(null);
  const [showAdvanced, setShowAdvanced] = useState(false);

  const selectedAudience =
    audienceOptions.find((option) => option.value === form.audience) ||
    audienceOptions[0];
  const canSend = form.title.trim() && form.body.trim();

  const customData = useMemo(() => {
    if (!form.dataKey.trim()) return {};
    return { [form.dataKey.trim()]: form.dataValue.trim() };
  }, [form.dataKey, form.dataValue]);

  const updateForm = (field) => (event) => {
    setForm((current) => ({ ...current, [field]: event.target.value }));
  };

  const submitNotification = async (dryRun = false) => {
    if (!canSend) {
      toast.error("Title and message are required");
      return;
    }

    try {
      setSending(true);
      const authConfig = await getJobPortalAuthConfig();
      const response = await axiosInstance.post(
        "/job-portal/admin/notifications/send",
        {
          audience: form.audience,
          title: form.title.trim(),
          body: form.body.trim(),
          actionUrl: form.actionUrl.trim(),
          imageUrl: form.imageUrl.trim(),
          data: customData,
          dryRun,
        },
        authConfig
      );

      const result = response.data?.data || {};
      setPreview(result);
      toast.success(
        dryRun
          ? `Preview ready for ${result.users || 0} users`
          : `Saved ${result.savedCount || 0}, pushed to ${result.successCount || 0} devices`
      );
      if (!dryRun) setForm(initialForm);
    } catch (error) {
      toast.error(error.response?.data?.message || "Unable to send notification");
    } finally {
      setSending(false);
    }
  };

  const clearForm = () => {
    setForm(initialForm);
    setPreview(null);
    setShowAdvanced(false);
  };

  return (
    <>
      <Sidebar />
      <main className="content">
        <Navbar />
        <div className="py-4">
          <div className="d-flex justify-content-between align-items-center flex-wrap gap-3 mb-4">
            <div>
              <div className="d-inline-flex align-items-center gap-2 px-3 py-2 rounded-pill bg-primary-subtle text-primary fw-semibold small mb-3">
                <i className="ri-notification-3-line" />
                Admin Broadcast
              </div>
              <h4 className="fw-bold mb-1">Send Notification</h4>
              <p className="text-muted mb-0">
                Write once, save it to user notifications, and deliver push alerts
                wherever FCM is available.
              </p>
            </div>
            <button
              type="button"
              className="btn btn-light border d-inline-flex align-items-center gap-2"
              onClick={clearForm}
            >
              <i className="ri-refresh-line" />
              Reset
            </button>
          </div>

          <div className="row g-4">
            <div className="col-xl-8">
              <div className="card border-0 shadow-sm">
                <div className="card-body p-4">
                  <div className="mb-4">
                    <div className="d-flex align-items-center gap-2 mb-3">
                      <span className="badge rounded-pill bg-primary">1</span>
                      <h5 className="mb-0">Choose Audience</h5>
                    </div>
                    <div className="row g-3">
                      {audienceOptions.map((option) => {
                        const active = form.audience === option.value;
                        return (
                          <div className="col-md-4" key={option.value}>
                            <button
                              type="button"
                              className={`w-100 h-100 text-start border rounded-3 p-3 bg-white ${
                                active ? "border-primary shadow-sm" : "border-light"
                              }`}
                              onClick={() =>
                                setForm((current) => ({
                                  ...current,
                                  audience: option.value,
                                }))
                              }
                            >
                              <div className="d-flex align-items-center gap-2 mb-2">
                                <span
                                  className={`rounded-circle d-inline-flex align-items-center justify-content-center ${
                                    active
                                      ? "bg-primary text-white"
                                      : "bg-light text-secondary"
                                  }`}
                                  style={{ width: 34, height: 34 }}
                                >
                                  <i className={option.icon} />
                                </span>
                                <span className="fw-semibold">{option.label}</span>
                              </div>
                              <div className="small text-muted">{option.note}</div>
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  <div className="mb-4">
                    <div className="d-flex align-items-center gap-2 mb-3">
                      <span className="badge rounded-pill bg-primary">2</span>
                      <h5 className="mb-0">Write Message</h5>
                    </div>
                    <div className="row g-3">
                      <div className="col-12">
                        <label className="form-label fw-semibold">Title</label>
                        <input
                          className="form-control form-control-lg"
                          placeholder="Example: New hiring update"
                          value={form.title}
                          onChange={updateForm("title")}
                          maxLength={80}
                        />
                        <div className="text-muted small mt-1">
                          {form.title.length}/80 characters
                        </div>
                      </div>
                      <div className="col-12">
                        <label className="form-label fw-semibold">Message</label>
                        <textarea
                          className="form-control"
                          placeholder="Keep it short and useful for the selected audience."
                          rows={6}
                          value={form.body}
                          onChange={updateForm("body")}
                          maxLength={240}
                        />
                        <div className="text-muted small mt-1">
                          {form.body.length}/240 characters
                        </div>
                      </div>
                      <div className="col-12">
                        <label className="form-label fw-semibold">Open Link</label>
                        <div className="input-group input-group-lg">
                          <span className="input-group-text bg-white">
                            <i className="ri-link" />
                          </span>
                          <input
                            className="form-control"
                            placeholder="/jobs or https://gojobify.com"
                            value={form.actionUrl}
                            onChange={updateForm("actionUrl")}
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="border-top pt-4">
                    <button
                      type="button"
                      className="btn btn-link p-0 text-decoration-none d-inline-flex align-items-center gap-2"
                      onClick={() => setShowAdvanced((current) => !current)}
                    >
                      <i
                        className={
                          showAdvanced
                            ? "ri-arrow-down-s-line"
                            : "ri-arrow-right-s-line"
                        }
                      />
                      Optional settings
                    </button>

                    {showAdvanced ? (
                      <div className="row g-3 mt-2">
                        <div className="col-12">
                          <label className="form-label">Image URL</label>
                          <input
                            className="form-control"
                            placeholder="https://..."
                            value={form.imageUrl}
                            onChange={updateForm("imageUrl")}
                          />
                        </div>
                        <div className="col-md-6">
                          <label className="form-label">Custom Data Key</label>
                          <input
                            className="form-control"
                            placeholder="campaign"
                            value={form.dataKey}
                            onChange={updateForm("dataKey")}
                          />
                        </div>
                        <div className="col-md-6">
                          <label className="form-label">Custom Data Value</label>
                          <input
                            className="form-control"
                            placeholder="summer_hiring"
                            value={form.dataValue}
                            onChange={updateForm("dataValue")}
                          />
                        </div>
                      </div>
                    ) : null}
                  </div>

                  <div className="d-flex flex-wrap gap-2 mt-4">
                    <button
                      type="button"
                      className="btn btn-outline-secondary d-inline-flex align-items-center gap-2"
                      disabled={sending || !canSend}
                      onClick={() => submitNotification(true)}
                    >
                      <i className="ri-eye-line" />
                      Preview Audience
                    </button>
                    <button
                      type="button"
                      className="btn btn-primary d-inline-flex align-items-center gap-2"
                      disabled={sending || !canSend}
                      onClick={() => submitNotification(false)}
                    >
                      <i className="ri-send-plane-fill" />
                      {sending ? "Sending..." : "Send Notification"}
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div className="col-xl-4">
              <div className="card border-0 shadow-sm mb-4">
                <div className="card-body p-4">
                  <div className="d-flex align-items-center justify-content-between mb-3">
                    <h5 className="mb-0">Live Preview</h5>
                    <span className="badge bg-light text-dark border">
                      {selectedAudience.label}
                    </span>
                  </div>
                  <div
                    className="border rounded-3 p-3 bg-light"
                    style={{ minHeight: 150 }}
                  >
                    <div className="d-flex gap-3">
                      <span
                        className="rounded-3 bg-primary text-white d-inline-flex align-items-center justify-content-center flex-shrink-0"
                        style={{ width: 42, height: 42 }}
                      >
                        <i className="ri-notification-3-line fs-5" />
                      </span>
                      <div className="min-w-0">
                        <div className="fw-semibold text-break">
                          {form.title || "Your notification title"}
                        </div>
                        <div className="text-muted small mt-1 text-break">
                          {form.body || "Message body will appear here."}
                        </div>
                        {form.actionUrl ? (
                          <div className="small text-primary mt-2 text-break">
                            {form.actionUrl}
                          </div>
                        ) : null}
                      </div>
                    </div>
                  </div>
                  <div className="small text-muted mt-3">
                    Users without FCM tokens will still receive this in
                    /notifications after it is saved.
                  </div>
                </div>
              </div>

              <div className="card border-0 shadow-sm">
                <div className="card-body p-4">
                  <h5 className="mb-3">Delivery Status</h5>
                  {preview ? (
                    <>
                      <div className="row g-2 text-center">
                        <div className="col-6">
                          <div className="border rounded-3 p-3">
                            <div className="h5 mb-0">{preview.users || 0}</div>
                            <div className="small text-muted">Matched Users</div>
                          </div>
                        </div>
                        <div className="col-6">
                          <div className="border rounded-3 p-3">
                            <div className="h5 mb-0">
                              {preview.savedCount || 0}
                            </div>
                            <div className="small text-muted">Saved to DB</div>
                          </div>
                        </div>
                        <div className="col-6">
                          <div className="border rounded-3 p-3">
                            <div className="h5 mb-0">{preview.tokens || 0}</div>
                            <div className="small text-muted">FCM Devices</div>
                          </div>
                        </div>
                        <div className="col-6">
                          <div className="border rounded-3 p-3">
                            <div className="h5 mb-0">
                              {preview.successCount || 0}
                            </div>
                            <div className="small text-muted">Push Sent</div>
                          </div>
                        </div>
                      </div>
                      {preview.fcmError ? (
                        <div className="alert alert-warning small mb-0 mt-3">
                          <i className="ri-error-warning-line me-1" />
                          Push failed, but DB notifications were saved.
                        </div>
                      ) : null}
                    </>
                  ) : (
                    <div className="text-muted small">
                      Preview the audience or send a notification to see matched
                      users, saved notifications, and FCM delivery here.
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </>
  );
};

export default NotificationCenter;
